/**
 * criteriaSuggestions — the PURE copy and rules behind "Suggested by AI from
 * Thrive" (epic `2026-10-30-ai-review-criteria-suggestions`, sub-tasks 03-05).
 *
 * No I/O, no React: `InlineCriteriaSuggestions` calls the API and renders, this
 * module decides what each line says and what can be added. Same split as
 * `carriedCriteria.ts`, whose merge is the one that actually writes the form.
 */
import type {
  CriteriaSignalGroup,
  CriteriaSignalStats,
  CriteriaSuggestion,
} from "@nu-assist/contracts";

import type { AiReviewApiError } from "../../../clients/aiReview.client";
import type { Criterion } from "./CriteriaPanel";

/** The groups the UI offers and asks for. Candidate rejections are left out
 *  (user decision, 2026-11-04, correcting 2026-11-03, which had dropped the
 *  client instead); the API still accepts `candidate`. */
export const SIGNAL_GROUPS = [
  "client",
  "recruiter",
] as const satisfies readonly CriteriaSignalGroup[];

export const GROUP_LABEL: Record<(typeof SIGNAL_GROUPS)[number], string> = {
  client: "Client",
  recruiter: "NU team",
};

type BasedOn = NonNullable<CriteriaSuggestion["based_on"]>[number];

const BASED_ON_LABEL: Record<BasedOn, string> = {
  client: "client rejections",
  recruiter: "NU team's rejections",
  candidate: "candidates who declined",
  advances: "candidates moved forward",
};

/** The key the server dedupes suggestions by (`_key` in
 *  `criteria_suggest_prompt.py`): spaces collapsed, lower case, no trailing
 *  period. */
function suggestionKey(text: string): string {
  return text.trim().replace(/\s+/g, " ").toLowerCase().replace(/\.+$/, "");
}

/**
 * Is this suggestion already a criterion in the form?
 *
 * Looser than `mergeCarriedCriteria` (trimmed, case-sensitive) on purpose:
 * since the form's criteria are no longer sent (2026-11-04), this is the only
 * check against them, so "Has CFO experience." in the form marks "has cfo
 * experience" as Added instead of offering a second copy (PR #1118 iter 9,
 * A9-2). Such a suggestion is never passed to the merge.
 */
export function isAlreadyInForm(text: string, existing: readonly Criterion[]): boolean {
  const wanted = suggestionKey(text);
  return existing.some((c) => suggestionKey(c.text) === wanted);
}

/** "Based on NU team's rejections and candidates moved forward". */
export function basedOnCopy(basedOn: readonly BasedOn[] = []): string {
  const labels = basedOn.map((b) => BASED_ON_LABEL[b]);
  if (labels.length === 0) return "";
  if (labels.length === 1) return `Based on ${labels[0]}`;
  return `Based on ${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

export function evidenceCopy(count = 0): string {
  return count === 1 ? "backed by 1 decision" : `backed by ${count} decisions`;
}

export function confidenceCopy(confidence: CriteriaSuggestion["confidence"] = "low"): string {
  return `${confidence[0].toUpperCase()}${confidence.slice(1)} confidence`;
}

/**
 * The counts line: "Client 18 rejections (7 with comments) · NU team 121 (40)
 * · 17 moved forward". Only the included groups; the first
 * one carries the units so the rest can stay short.
 */
export function countsCopy(stats: CriteriaSignalStats): string {
  const parts: string[] = [];
  for (const group of SIGNAL_GROUPS) {
    const g = stats.by_group?.[group];
    if (!g?.included) continue;
    parts.push(
      parts.length === 0
        ? `${GROUP_LABEL[group]} ${g.rejections ?? 0} rejections (${g.with_text ?? 0} with comments)`
        : `${GROUP_LABEL[group]} ${g.rejections ?? 0} (${g.with_text ?? 0})`
    );
  }
  const advanced = Object.values(stats.advances ?? {}).reduce((sum, n) => sum + n, 0);
  if (advanced > 0) parts.push(`${advanced} moved forward`);
  return parts.join(" · ");
}

/** The noise line, or null when nothing was dropped. */
export function noiseCopy(stats: CriteriaSignalStats): string | null {
  const bulk = stats.dropped_bulk ?? 0;
  const boilerplate = stats.dropped_boilerplate ?? 0;
  const ignored = bulk + boilerplate;
  if (ignored === 0) return null;
  const causes: string[] = [];
  if (bulk > 0) causes.push("rejected in bulk");
  if (boilerplate > 0) causes.push("notes copied from a previous search");
  const noun = ignored === 1 ? "decision" : "decisions";
  return `${ignored} ${noun} ignored: ${causes.join(" or ")}.`;
}

/** Why the list is empty, from the stats. With too little text (`empty`
 *  quality) the model was not asked at all. */
export function emptyCopy(stats: CriteriaSignalStats): string {
  const read = stats.sent_to_model ?? 0;
  if (read === 0) {
    return "No decision in the selected groups has a comment or assessment to learn from.";
  }
  if (stats.quality === "empty") {
    const noun = read === 1 ? "decision has" : "decisions have";
    return `Only ${read} ${noun} a comment or assessment in the selected groups: too few to suggest from yet.`;
  }
  return "The decisions in this search don't point to anything a profile can show.";
}

/**
 * The subtitle: when the search's decisions were read from Thrive. Answers are
 * kept up to 24 h, so this says how fresh they are ("as of Nov 2, 2:05 PM").
 * `timeZone` is for tests; the browser's own zone otherwise.
 */
export function asOfCopy(generatedAt: string | null | undefined, timeZone?: string): string {
  const base = "Based on the decisions recorded in Thrive";
  const at = generatedAt ? new Date(generatedAt) : null;
  if (!at || Number.isNaN(at.getTime())) return `${base} for this search.`;
  const when = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  })
    .format(at)
    // ICU puts a narrow no-break space before AM/PM.
    .replace(/\u202f/g, " ");
  return `${base} as of ${when}.`;
}

/** One sentence per failure, keyed on the API's `detail.reason`. */
export function suggestErrorCopy(error: AiReviewApiError): string {
  switch (error.detail?.reason) {
    case "import_has_no_search":
      return "This list is not linked to a Thrive search.";
    case "thrive_unavailable":
    case "thrive_unconfigured":
      return "Couldn't read the search's decisions from Thrive.";
    case "thrive_timeout":
      return "Thrive took too long to answer.";
    case "invalid_output":
    case "model_unavailable":
      return "The AI couldn't produce suggestions this time.";
    case "suggestion_timeout":
      return "The suggestions took too long.";
    default:
      return "Couldn't get suggestions.";
  }
}

/** The footer note when the cap refuses some of the ticked suggestions. */
export function doesNotFitCopy(dropped: number, max: number): string | null {
  if (dropped === 0) return null;
  const noun = dropped === 1 ? "criterion doesn't" : "criteria don't";
  return `${dropped} ${noun} fit: a review takes at most ${max} criteria.`;
}

export function addButtonCopy(count: number): string {
  return count === 1 ? "Add 1 criterion" : `Add ${count} criteria`;
}

/**
 * What a run records about a criterion's origin (2026-11-03, measuring how the
 * suggestions are used): an AI-suggested row says so, and whether its text was
 * changed from the suggestion. A manual row adds nothing, so its payload is
 * the one it always was.
 *
 * @param ai  criterion id -> the text as suggested (the page's record)
 */
export function criterionOrigin(
  criterion: Pick<Criterion, "id" | "text">,
  ai: ReadonlyMap<string, string>
): { source?: "ai_suggested"; edited?: boolean } {
  const suggested = ai.get(criterion.id);
  if (suggested === undefined) return {};
  return { source: "ai_suggested", edited: criterion.text.trim() !== suggested.trim() };
}
